import * as React from 'react';

import { cx } from '../../utils/cx';
import Label from '../Input/Label';

interface IYesNoToggleBaseProps {
  /** `null` renders an unselected pair — neither option highlighted. */
  value: boolean | null;
  onChange: (value: boolean) => void;
  className?: string;
}

interface IYesNoToggleWithLabelProps extends IYesNoToggleBaseProps {
  /** Visible field label rendered above the toggle; also names the group for screen readers. */
  label: string;
  ariaLabel?: never;
}

interface IYesNoToggleWithAriaLabelProps extends IYesNoToggleBaseProps {
  /** Screen-reader-only name for the group when there's no visible label — pass the question text. */
  ariaLabel: string;
  label?: never;
}

/** Exactly one of `label` (visible) or `ariaLabel` (screen-reader-only) must be provided. */
type IYesNoToggleProps = IYesNoToggleWithLabelProps | IYesNoToggleWithAriaLabelProps;

/**
 * Full-width Yes/No segmented control. Selection is drawn with an outline ring
 * (not a border-width swap) so picking an option doesn't shift the box model
 * and bounce the row. Unlike {@link ButtonGroup}, the two options are gapped,
 * individually rounded cards rather than a joined segmented control.
 *
 * Pass `label` to render a bold field label above the toggle (it also names the
 * group for screen readers via `aria-labelledby`); pass `ariaLabel` instead when
 * the question is rendered elsewhere and only a screen-reader name is needed.
 */
const YesNoToggle = ({ value, onChange, label, ariaLabel, className }: IYesNoToggleProps) => {
  const labelId = React.useId();

  const renderOption = (optionValue: boolean, optionLabel: string) => {
    const isSelected = value === optionValue;
    return (
      <button
        type="button"
        onClick={() => onChange(optionValue)}
        aria-pressed={isSelected}
        className={cx(
          'bg-primary shadow-xs text-md text-secondary flex flex-1 items-center justify-center rounded-xl px-4 py-3 font-medium outline-2 -outline-offset-2',
          isSelected ? 'outline-brand' : 'outline-secondary'
        )}
      >
        {optionLabel}
      </button>
    );
  };

  const group = (
    <div
      className={cx('flex w-full gap-2', label === undefined && className)}
      role="radiogroup"
      aria-label={label === undefined ? ariaLabel : undefined}
      aria-labelledby={label === undefined ? undefined : labelId}
    >
      {renderOption(true, 'Yes')}
      {renderOption(false, 'No')}
    </div>
  );

  if (label === undefined) {
    return group;
  }

  return (
    <div className={cx('flex w-full flex-col gap-1.5', className)}>
      <Label id={labelId}>{label}</Label>
      {group}
    </div>
  );
};
YesNoToggle.displayName = 'YesNoToggle';

export default YesNoToggle;
